import logo from "./logo.svg";
import "./App.css";
import PrimaryLayout from "./components/primaryLayout";
import { useState, useEffect } from "react";
import { getOktaToken, getRequestId } from "./common/token";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  REACT_APP_API,
  REACT_APP_API_OKTA,
  REACT_APP_API_TENANTS_URL,
  REACT_APP_REDIRECT_URL,
} from "./constants";
import { logOutUser } from "./common";
import { Button, Result, Spin, message,  ConfigProvider, theme as antdTheme } from "antd";
import { authorize } from "./common/SecureRoute";
import UnAuthorizeRoutes from "./components/unauthorized/UnAuthorizeRoutes";
import { ReactComponent as ACCESSDENIED } from "./assets/access-denied.svg";

import MfaCacheNotification from "./components/unauthorized/tenant/MfaCacheNotification";

function App() {
  const navigate = useNavigate();
  const [appLoading, setAppLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  
  const authToken = localStorage.getItem("token");

  const [ssoMfaAttemptEmail, setSsoMfaAttemptEmail] = useState(null);
  const [ssoMfaAttemptToken, setSsoMfaAttemptToken] = useState(null);
  const [isSSOMFAFailureTimeout, setIsSSOMFAFailureTimeout] = useState(false);
  const [isSSOMFAFailureDenied, setIsSSOMFAFailureDenied] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // State to show MfaCacheNotification during /okta/ssomfa call
  const [isAwaitingSSOMFAConfirmation, setIsAwaitingSSOMFAConfirmation] = useState(false);

  let path = window.location.pathname;

  const handleSsoBackToLogin = async () => {
    // ... (implementation from previous response, unchanged)
    try {
      const url = localStorage.getItem("URL");
      const email = ssoMfaAttemptEmail;
      const currentSsoToken = ssoMfaAttemptToken;

      if (!url || !email || !currentSsoToken) {
        messageApi.open({
          type: "error",
          content: "SSO session data is corrupted. Please try logging in again.",
        });
        logOutUser(); 
        return;
      }

      const payload = { url, email, token: currentSsoToken };
      const response = await axios.post(`${REACT_APP_API}/v1/backToLogin`, payload);
      const authMethod = localStorage.getItem("authenticationmethod")?.toUpperCase();

      if (response.status === 200) {
        if (authMethod === "OKTA" || authMethod === "ONELOGIN") {
          window.location.href = `https://${url}`;
        } else {
          navigate("/tenant/login");
        }
      }
    } catch (error) {
      console.error("Error in SSO backToLogin:", error);
      messageApi.open({
        type: "error",
        content: "Failed to process SSO back to login request. Please try again.",
      });
      logOutUser();
    } finally {
      setIsSSOMFAFailureTimeout(false);
      setIsSSOMFAFailureDenied(false);
      setSsoMfaAttemptEmail(null);
      setSsoMfaAttemptToken(null);
    }
  };

  useEffect(() => {
    // setAppLoading(true); // Moved to be more granular
    localStorage.setItem(
      "URL",
      window.location.host === "localhost:5173" ? `${REACT_APP_REDIRECT_URL}` : window.location.host
    );

    const fetchToken = async (sessionToken) => {
      const handleCheckSSOMFAEnable = (userDataFromGetDetails, currentSessionToken) => {
        // At this point, appLoading was true from getEmailIdAndCheckMFA.
        // Now, transition from generic appLoading to specific MfaCacheNotification.
        setIsAwaitingSSOMFAConfirmation(true);
        setAppLoading(false); // Turn off generic spinner; MfaCacheNotification will display.

        const payload = {
          url: localStorage.getItem("URL"),
          email: userDataFromGetDetails?.Username,
          token: currentSessionToken,
          RequestID: "",
        };

        axios
          .post(`${REACT_APP_API}/okta/ssomfa`, payload)
          .then(({ data: ssoMfaResponseData }) => {
            setIsAwaitingSSOMFAConfirmation(false);
            // requiredAccess will setAppLoading(true) for its own operations.
            localStorage.setItem("token", currentSessionToken);
            requiredAccess(currentSessionToken);
          })
          .catch((err) => {
            setIsAwaitingSSOMFAConfirmation(false);
            setAppLoading(false); // Ensure generic spinner is off to show error screen.
            console.error("SSO MFA Check Error:", err);
            setSsoMfaAttemptEmail(userDataFromGetDetails?.Username);
            setSsoMfaAttemptToken(currentSessionToken);
            if (err?.response?.status === 504) {
              setIsSSOMFAFailureTimeout(true);
            } else {
              setIsSSOMFAFailureDenied(true);
            }
          });
      };
      
      const getEmailIdAndCheckMFA = () => {
        setAppLoading(true); // Start loading for the getEmailId part
        let domainUrl = localStorage.getItem("URL");

        axios
          .post(
            `${REACT_APP_API}/authnz/getUserDetails`,
            {},
            {
              headers: {
                withCredentials: true,
                "X-Authorization": `${sessionToken}&DOMAIN&${domainUrl}`,
                "X-RequestUrl": domainUrl,
              },
            }
          )
          .then(({ data }) => {
            if (data?.Validation === true && data?.Status === "Success") {
              localStorage.setItem("UserName", data?.Username);
              // appLoading is still true, proceed to MFA check
              handleCheckSSOMFAEnable(data, sessionToken);
            } else {
              setAppLoading(false); // Stop loading if getUserDetails is not valid/success
              logOutUser();
            }
          })
          .catch((err) => {
            setAppLoading(false); // Stop loading on error
            if (
              err?.response?.status === 401 &&
              err.response?.data?.Validation === false &&
              err.response?.data?.Message === "User Does not exist contact your administrator"
            ) {
              setIsUnauthorized(true);
            } else {
              logOutUser();
            }
          });
      };

      getEmailIdAndCheckMFA();
    };

    if (!authToken) {
      const params = new URLSearchParams(window.location.search);
      let session = params.get("session");
      getRequestId();
      if (session) {
        // fetchToken will manage its own appLoading states internally
        fetchToken(session);
      } else {
        setAppLoading(false); // No session, no token, stop initial load.
        // ... (rest of the logic for no session, no authToken)
        // This part remains as in your previous code.
        localStorage.setItem(
          "URL",
          window.location.host === "localhost:5173"
            ? "default.devsetup.dev.authnull.com"
            : window.location.host
        );
        if (["/org/signup", "/org/login", "/tenant/login", "/tenant/signup"].includes(path)) {
          // For these paths, don't automatically redirect or check auth method
          // setAppLoading(false) was already called
          return;
        } else if (path === "/") {
          if (localStorage.getItem("URL") === "app.authnull.com") {
            navigate("/org/login");
          } else {
            CheckAuthnMethod(); // This will manage its own loading
          }
        } else {
          // For other paths without session/token, may need specific handling or rely on UnAuthorizeRoutes
          // setAppLoading(false) was already called
        }
      }
    } else { // authToken exists
      requiredAccess(authToken); // This will manage its own appLoading
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, path]); // navigate removed as it's stable, path added for initial routing logic

  let reqId = localStorage.getItem("requestId");

  const CheckAuthnMethod = () => {
    // ... (implementation unchanged, ensure it manages setAppLoading)
    setAppLoading(true);
    const data = { url: localStorage.getItem("URL"), requestId: reqId };
    axios.post(`${REACT_APP_API_TENANTS_URL}/getAuthMethod`, data)
      .then((res) => {
        const method = res.data?.authenticationmethod;
        localStorage.setItem("authenticationmethod", method);
        const ssoUrl = res.data?.ssoUrl;
        if (method) {
          const upperCaseMethod = method.toUpperCase();
          localStorage.setItem("logMethod", upperCaseMethod);
          switch (upperCaseMethod) {
            case "NORMAL": navigate("/tenant/login"); break;
            case "ONELOGIN": case "OKTA": case "SAML":
              if (ssoUrl) { window.location.replace(ssoUrl); }
              else { navigate("/tenant/login"); }
              break;
            default: navigate("/tenant/login"); break;
          }
        } else { navigate("/tenant/login"); }
      })
      .catch((error) => { navigate("/tenant/login"); })
      .finally(() => setAppLoading(false));
  };

  const fetchTenantDetail = (data, token) => { /* ... (unchanged) ... */ 
    let payload = { email: data?.Username, tenantId: data?.DomainID, orgId: data?.OrgID };
    axios.post(`${REACT_APP_API_TENANTS_URL}/getTenantDetail`, payload, { headers: { withCredentials: true, "X-Authorization": token } })
      .then((res) => {
        let tenant = res?.data?.data;
        localStorage.setItem("Tenantname", tenant?.tenantname);
        localStorage.setItem("OrgName", tenant?.organizationName);
        localStorage.setItem("timezone", tenant?.timezone || "America/Los_Angeles");
      });
  };
  
  const requiredAccess = (token) => { /* ... (unchanged, ensure it manages setAppLoading) ... */
    setAppLoading(true);
    let domainUrl = localStorage.getItem("URL");
    let AUTHORIZATION = (["localhost:5173", "app.authnull.com", "dev.authnull.com"].includes(domainUrl))
      ? `${token}&DOMAIN&${domainUrl}&ORGLOGIN&`
      : `${token}&DOMAIN&${domainUrl}`;

    axios.post(`${REACT_APP_API}/authnz/getUserDetails`, {}, { headers: { withCredentials: true, "X-Authorization": AUTHORIZATION, "X-RequestUrl": localStorage.getItem("URL") } })
      .then(({ data }) => {
        if (data?.Validation === true && data?.Status === "Success") {
          localStorage.setItem("UserRole", data?.UserRole);
          localStorage.setItem("UserName", data?.Username);
          localStorage.setItem("userId", data?.UserID);
          localStorage.setItem("DomainId", data?.DomainID);
          localStorage.setItem("OrgId", data?.OrgID);
          const XAUTHORIZATION = `${token}&DOMAIN&${domainUrl}&ORGID&${data?.OrgID}`;
          if (data?.UserRole === "ADMIN" || data?.UserRole === "ENDUSER") fetchTenantDetail(data, XAUTHORIZATION);
          
          if (!authorize(data?.UserRole, window.location.pathname)) {
            if (data?.UserRole === "ADMIN") navigate("/");
            else if (data?.UserRole === "ENDUSER") navigate("/endUser/endpoints");
            else if (data?.UserRole === "SUPERADMIN") fetchTenent(XAUTHORIZATION);
            else navigate("/");
          }
        } else { logOutUser(); }
      })
      .catch((err) => {
        if (err?.response?.status === 401 && err.response?.data?.Validation === false && err.response?.data?.Message === "User Does not exist contact your administrator") {
          setIsUnauthorized(true);
        } else { logOutUser(); }
      })
      .finally(() => setAppLoading(false));
  };

  const fetchTenent = (token) => { /* ... (unchanged) ... */
    let data = { email: localStorage.getItem("UserName") };
    axios.post(`${REACT_APP_API_TENANTS_URL}/tenantlist`, data, { headers: { withCredentials: true, "X-Authorization": token } })
      .then((res) => {
        let tenant = res?.data?.data;
        if (tenant.length < 1) navigate("/tenant/add");
        else navigate("/org/dashboard");
      });
   };

  // Conditional Rendering Logic
  if (isAwaitingSSOMFAConfirmation) {
    // MfaCacheNotification takes over the screen, isLoading={true} to indicate activity
    return (
      <div className="app-container">
        {contextHolder}
        <MfaCacheNotification isLoading={true} />
      </div>
    );
  }

  if (isSSOMFAFailureTimeout) {
    return (
      <div className="app-container">
        {contextHolder}
        <div className="login-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div className="login-box" style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '8px', background: '#fff' }}>
            <Result status="403" title="MFA Verification Timeout" subTitle="Request timed out while waiting for MFA approval. Please try again."
              extra={ <Button type="primary" onClick={() => { setIsSSOMFAFailureTimeout(false); handleSsoBackToLogin(); }}>Back to Login</Button> }
            />
          </div>
        </div>
      </div>
    );
  }

  if (isSSOMFAFailureDenied) {
     return (
      <div className="app-container">
        {contextHolder}
        <div className="login-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div className="login-box" style={{ textAlign: 'center', padding: '20px', border: '1px solid #ccc', borderRadius: '8px', background: '#fff' }}>
            <h2 style={{ marginBottom: "2rem" }}>Unable to Verify SSO MFA</h2>
            <span style={{ display: "flex", justifyContent: "center" }}><ACCESSDENIED style={{ height: 200, width: 200 }} /></span>
            <Button type="primary" onClick={() => { setIsSSOMFAFailureDenied(false); handleSsoBackToLogin(); }} style={{ marginTop: "3rem", marginBottom: "1rem" }}>Back to Login</Button>
          </div>
        </div>
      </div>
    );
  }

  if (appLoading) {
    return (
      <div className="app-container">
        {contextHolder}
        <div className="app-loading"><Spin size="large" /></div>
      </div>
    );
  }
  
  if (isUnauthorized) {
    return (
      <div className="app-container">
        {contextHolder}
        <Result status="403" title="You do not have permission to access this application." subTitle="Please contact your Site Administrator(s) to request access."
          extra={ <Button type="primary" onClick={() => { localStorage.clear(); if (REACT_APP_API_OKTA) { window.location.href = `${REACT_APP_API_OKTA}/okta/RedirectLink`; } else { navigate("/tenant/login"); } }}>Return to Login</Button> }
        />
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
        token: {
          fontSize: 14,
          fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'`,
          fontWeightStrong: 500,
        },
      }}
    >
      <div className="app-conatiner"> {/* Note: Original had app-conatiner, changed to app-container for consistency if a typo */}
        {contextHolder}
        {!authToken ? (
          <UnAuthorizeRoutes />
        ) : !appLoading && authToken ? (
          <PrimaryLayout />
        ) : isUnauthorized && !appLoading ? (
          <Result
            status="403"
            title={`You do not have permission to access this application.`}
            subTitle="Please contact your Site Administrator(s) to request access."
            extra={
              <Button
                type="primary"
                onClick={() => {
                  localStorage.clear();
                  navigate(`${REACT_APP_API_OKTA}/okta/RedirectLink`);
                }}
              >
                Return to the login
              </Button>
            }
          />
        ) : (
          <div className="app-loading">
            <Spin />
          </div>
        )}
      </div>
    </ConfigProvider>
  );
}

export default App;


// Add global styles for links
const style = document.createElement('style');
style.innerHTML = `
  a {
    color: #1a73e8 !important; /* Google blue */
    text-decoration: underline !important;
    font-weight: 500 !important;
  }
`;
document.head.appendChild(style);